import { defineStore } from "pinia";
import { ref } from "vue";
import type {
  SummaryResult,
  EnhancedSummaryResult,
  FlashcardResult,
} from "@/services/geminiService";
import { summaryService } from "@/services/summaryService";
import { flashcardService } from "@/services/flashcardService";
import { auth } from "@/firebase";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";

export const useAIContentStore = defineStore("aiContent", () => {
  const generatedContent = ref<SummaryResult | EnhancedSummaryResult | null>(null);
  const generatedFlashcards = ref<FlashcardResult | null>(null);
  const isLoading = ref(false);
  const lastGeneratedDate = ref<string>("");
  const sourceFileName = ref<string>("");
  const contentType = ref<"file" | "text">("text");
  const originalContent = ref<string>(""); // Store original content for Firebase saving
  const savedSummaryId = ref<string | null>(null); // Track if summary is saved to Firebase
  const savedFlashcardsId = ref<string | null>(null); // Track if flashcards are saved to Firebase

  const setGeneratedContent = async (
    content: SummaryResult | EnhancedSummaryResult,
    fileName?: string,
    originalText: string = "",
    sourceType: "pdf" | "txt" | "docx" | "note" = "note",
    uploadedFile: File | null = null,
    sessionId?: string // ✅ NEW: Optional session ID from completed session
  ) => {
    generatedContent.value = content;
    lastGeneratedDate.value = new Date().toLocaleDateString();
    sourceFileName.value = fileName || "Pasted Notes";
    contentType.value = fileName ? "file" : "text";

    // ⚡ OPTIMIZATION 7: Store only first 5000 chars of original content to reduce memory
    originalContent.value =
      originalText.length > 5000
        ? originalText.substring(0, 5000) + "... [truncated for memory]"
        : originalText;

    // Automatically save to Firebase when content is generated
    try {
      isLoading.value = true;
      logInfo("Starting AI content Firebase save process");
      logDebug("Content details", {
        topic: content.topic,
        subject: content.subject,
        sourceType: sourceType,
        hasFilename: !!fileName,
        hasFile: !!uploadedFile,
      });

      if (!auth.currentUser) {
        logError("No authenticated user found in store");
        throw new Error("Authentication required to save to library");
      }

      logDebug("User authentication verified for save");

      const summaryId = await summaryService.saveSummary(
        content,
        fileName || null,
        originalText, // ⚡ Send full content to Firebase (stored on server, not in memory)
        sourceType,
        uploadedFile,
        sessionId // Pass session ID from completed session
      );
      savedSummaryId.value = summaryId;
      logSuccess("✅ AI SUMMARY SAVED TO FIREBASE", {
        summaryId: summaryId.substring(0, 8) + "***",
        topic: content.topic,
        subject: content.subject,
        sessionId: sessionId || "none",
      });
    } catch (error) {
      logError("❌ FAILED TO SAVE AI SUMMARY TO FIREBASE", error);
      console.error("🚨 Summary save error details:", error);
      // Don't throw error here to avoid breaking the UI flow
      // The summary is still available in the store even if Firebase save fails
    } finally {
      isLoading.value = false;
    }
  };

  const setGeneratedFlashcards = async (
    flashcards: FlashcardResult,
    fileName?: string,
    originalText: string = "",
    sourceType: "pdf" | "txt" | "docx" | "note" = "note",
    uploadedFile: File | null = null,
    sessionId?: string // ✅ NEW: Optional session ID from completed session
  ) => {
    generatedFlashcards.value = flashcards;
    lastGeneratedDate.value = new Date().toLocaleDateString();
    sourceFileName.value = fileName || "Pasted Notes";
    contentType.value = fileName ? "file" : "text";

    // ⚡ OPTIMIZATION 8: Store only first 5000 chars of original content to reduce memory
    originalContent.value =
      originalText.length > 5000
        ? originalText.substring(0, 5000) + "... [truncated for memory]"
        : originalText;

    // Clear any existing summary content since we're generating flashcards
    generatedContent.value = null;
    savedSummaryId.value = null;

    // Automatically save flashcards to Firebase flashcards collection
    try {
      isLoading.value = true;
      logInfo("Starting flashcards Firebase save process");
      logDebug("Flashcards details", {
        topic: flashcards.topic,
        subject: flashcards.subject,
        flashcardCount: flashcards.flashcards.length,
        sourceType: sourceType,
        hasFilename: !!fileName,
        hasFile: !!uploadedFile,
      });

      if (!auth.currentUser) {
        logError("No authenticated user found in store");
        throw new Error("Authentication required to save to library");
      }

      logDebug("User authentication verified for flashcards save");

      // Use the dedicated flashcard service to save to flashcards collection
      const flashcardsId = await flashcardService.saveFlashcards(
        flashcards,
        fileName || null,
        originalText, // ⚡ Send full content to Firebase (stored on server, not in memory)
        sourceType,
        uploadedFile,
        sessionId // Pass session ID from completed session
      );
      savedFlashcardsId.value = flashcardsId;
      logSuccess("✅ FLASHCARDS SAVED TO FIREBASE", {
        flashcardsId: flashcardsId.substring(0, 8) + "***",
        topic: flashcards.topic,
        subject: flashcards.subject,
        cardCount: flashcards.flashcards.length,
        sessionId: sessionId || "none",
      });
    } catch (error) {
      logError("❌ FAILED TO SAVE FLASHCARDS TO FIREBASE", error);
      console.error("🚨 Flashcards save error details:", error);
      // Don't throw error here to avoid breaking the UI flow
      // The flashcards are still available in the store even if Firebase save fails
    } finally {
      isLoading.value = false;
      // ⚡ OPTIMIZATION 9: Clear large content from memory after save
      if (originalText.length > 10000) {
        logDebug("⚡ Clearing large original content from memory after save");
      }
    }
  };

  const clearContent = () => {
    generatedContent.value = null;
    generatedFlashcards.value = null;
    lastGeneratedDate.value = "";
    sourceFileName.value = "";
    contentType.value = "text";
    originalContent.value = "";
    savedSummaryId.value = null;
    savedFlashcardsId.value = null;

    // ⚡ OPTIMIZATION 10: Force garbage collection hint
    logDebug("🧹 Content store cleared - memory should be freed");
  };

  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  return {
    generatedContent,
    generatedFlashcards,
    isLoading,
    lastGeneratedDate,
    sourceFileName,
    contentType,
    originalContent,
    savedSummaryId,
    savedFlashcardsId,
    setGeneratedContent,
    setGeneratedFlashcards,
    clearContent,
    setLoading,
  };
});
